import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { MapPin, Phone, Search, X, AlertCircle, Loader, Navigation, Store } from "lucide-react";

const Sucursales = () => {
    const { user } = useUserAuth();
    const navigate = useNavigate();
    const [stores, setStores] = useState([]);
    const [filteredStores, setFilteredStores] = useState([]);
    const [loadingStores, setLoadingStores] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [loadingLocation, setLoadingLocation] = useState(false);
    const [nearestStoreId, setNearestStoreId] = useState(null);
    const [showSuccessImage, setShowSuccessImage] = useState(false);

    // Fetch stores from Firestore
    useEffect(() => {
        const fetchStores = async () => {
            try {
                if (!db) {
                    throw new Error("Firestore instance is not initialized");
                }
                const querySnapshot = await getDocs(collection(db, "stores"));
                const storesData = [];
                querySnapshot.forEach((doc) => {
                    storesData.push({ id: doc.id, ...doc.data() });
                });
                setStores(storesData);
                setFilteredStores(storesData);
                setError(null);
            } catch (error) {
                console.error("Error fetching stores:", error);
                setError("Error al cargar las sucursales. Intenta de nuevo.");
            } finally {
                setLoadingStores(false);
            }
        };

        fetchStores();
    }, []);

    // Show location modal after stores load
    useEffect(() => {
        if (!loadingStores && stores.length > 0) {
            const timer = setTimeout(() => {
                setShowLocationModal(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [loadingStores, stores.length]);

    // Filter stores based on search query
    useEffect(() => {
        if (searchQuery.trim() === "") {
            setFilteredStores(stores);
        } else {
            const filtered = stores.filter(
                (store) =>
                    store.storeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    store.address.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredStores(filtered);
        }
    }, [searchQuery, stores]);

    // Calculate distance using Haversine formula
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371e3;
        const φ1 = (lat1 * Math.PI) / 180;
        const φ2 = (lat2 * Math.PI) / 180;
        const Δφ = ((lat2 - lat1) * Math.PI) / 180;
        const Δλ = ((lon2 - lon1) * Math.PI) / 180;

        const a =
            Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    };

    // Format distance for display
    const formatDistance = (meters) => {
        if (meters < 1000) {
            return `${Math.round(meters)} m`;
        }
        return `${(meters / 1000).toFixed(1)} km`;
    };

    // Request location permission
    const requestLocationPermission = async () => {
        try {
            setLoadingLocation(true);

            if (!navigator.geolocation) {
                alert("Tu navegador no soporta geolocalización.");
                setLoadingLocation(false);
                setShowLocationModal(false);
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const location = {
                        coords: {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                        },
                    };
                    setUserLocation(location);

                    const storesWithDistance = stores.map((store) => {
                        const distance = calculateDistance(
                            location.coords.latitude,
                            location.coords.longitude,
                            parseFloat(store.latitude),
                            parseFloat(store.longitude)
                        );
                        return { ...store, distance };
                    });

                    const sortedStores = [...storesWithDistance].sort(
                        (a, b) => (a.distance || Infinity) - (b.distance || Infinity)
                    );

                    setStores(sortedStores);
                    setFilteredStores(sortedStores);

                    const nearest = sortedStores[0];
                    if (nearest && nearest.distance !== undefined) {
                        setNearestStoreId(nearest.id);
                        setShowSuccessImage(true);

                        setTimeout(() => {
                            setShowSuccessImage(false);
                        }, 3000);

                        setTimeout(() => {
                            const element = document.getElementById(`store-${nearest.id}`);
                            if (element) {
                                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }, 1000);
                    }

                    setLoadingLocation(false);
                    setShowLocationModal(false);
                },
                (error) => {
                    console.error("Error getting location:", error);
                    alert(
                        "No se pudo obtener tu ubicación. Por favor, verifica los permisos de geolocalización."
                    );
                    setLoadingLocation(false);
                    setShowLocationModal(false);
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 1000 }
            );
        } catch (error) {
            console.error("Error requesting location:", error);
            alert("Error al obtener la ubicación. Intenta de nuevo.");
            setLoadingLocation(false);
            setShowLocationModal(false);
        }
    };

    // Open Google Maps
    const openInMaps = (store) => {
        const latitude = store.latitude;
        const longitude = store.longitude;
        const label = encodeURIComponent(store.storeName);
        const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}&query_place_id=${label}`;

        window.open(url, "_blank");
    };

    // Handle order navigation
    const handleOrder = (storeId) => {
        navigate(`/order/${storeId}`);
    };

    // Handle store card click
    const handleStorePress = (store) => {
        navigate(`/order/${store.id}`);
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
            {/* Success Image Animation */}
            {showSuccessImage && (
                <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none bg-black/20 backdrop-blur-sm">
                    <img
                        src="https://firebasestorage.googleapis.com/v0/b/newpollofeliz.appspot.com/o/PROMOS%2FYA_ENCONTRE_UNA_SUCURSAL__JALATE-removebg-preview.png?alt=media&token=64a69918-79e2-4cb6-a0ff-24bdb1693c89"
                        alt="Encontré tu sucursal"
                        className="w-94 sm:w-90 md:w-96 lg:w-[500px] h-auto animate-slideUp drop-shadow-2xl"
                    />
                </div>
            )}

            {/* Fixed Search Bar - Modern sticky header */}
            <div className="fixed top-16 sm:top-20 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg shadow-lg border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
                    {/* Search and Location Row */}
                    <div className="flex items-center gap-2 sm:gap-3">
                        {/* Search Input */}
                        <div className="flex-1 bg-gray-50 rounded-xl flex items-center px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-transparent focus-within:border-[#FF0000] transition-all">
                            <Search className="w-4 h-4 sm:w-5 sm:h-5 text-[#FF0000] mr-2 sm:mr-3 flex-shrink-0" strokeWidth={2} />
                            <input
                                type="text"
                                placeholder="Buscar sucursal..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="flex-1 bg-transparent text-gray-800 text-sm sm:text-base font-medium placeholder-gray-400 focus:outline-none"
                            />
                            {searchQuery.length > 0 && (
                                <button
                                    onClick={() => setSearchQuery("")}
                                    className="ml-2 bg-gray-200 hover:bg-gray-300 p-1.5 rounded-lg transition-colors flex-shrink-0"
                                >
                                    <X className="w-4 h-4 text-gray-600" />
                                </button>
                            )}
                        </div>

                        {/* Location Button */}
                        <button
                            onClick={() => setShowLocationModal(true)}
                            className="bg-[#FF0000] text-white px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl font-semibold hover:bg-[#CC0000] transition-all flex items-center whitespace-nowrap shadow-lg hover:shadow-xl"
                        >
                            <Navigation className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" strokeWidth={2} />
                            <span className="hidden sm:inline text-sm">Mi Ubicación</span>
                        </button>
                    </div>

                    {/* Results Counter */}
                    {!loadingStores && !error && (
                        <div className="mt-2 sm:mt-3 text-gray-600 text-xs sm:text-sm font-medium">
                            {searchQuery.length > 0 ? (
                                <span>
                                    {filteredStores.length} sucursal{filteredStores.length !== 1 ? "es" : ""} encontrada{filteredStores.length !== 1 ? "s" : ""}
                                </span>
                            ) : (
                                <span>{stores.length} sucursales disponibles</span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="pt-32 sm:pt-36 pb-6 sm:pb-8 px-3 sm:px-4">
                <div className="max-w-7xl mx-auto">
                    {/* Location Modal - Modern glassmorphism */}
                    {showLocationModal && (
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                            <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden animate-slideIn">
                                <div className="p-6 sm:p-8">
                                    <div className="text-center mb-6">
                                        <div className="bg-gradient-to-br from-[#FF0000] to-[#CC0000] p-4 rounded-2xl inline-block mb-4 shadow-lg">
                                            <MapPin className="w-10 h-10 sm:w-12 sm:h-12 text-white" strokeWidth={2} />
                                        </div>
                                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3">
                                            Encuentra tu sucursal
                                        </h2>
                                        <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                                            Activa tu ubicación para encontrar la sucursal más cercana a ti
                                        </p>
                                    </div>

                                    <div className="bg-gray-50 rounded-xl p-4 sm:p-5 mb-6 space-y-3">
                                        <div className="flex items-center">
                                            <div className="bg-[#FF0000] p-2 rounded-lg mr-3 flex-shrink-0">
                                                <MapPin className="w-5 h-5 text-white" strokeWidth={2} />
                                            </div>
                                            <p className="text-sm text-gray-700 font-medium">
                                                Encuentra la sucursal más cercana
                                            </p>
                                        </div>
                                        <div className="flex items-center">
                                            <div className="bg-[#FF0000] p-2 rounded-lg mr-3 flex-shrink-0">
                                                <Navigation className="w-5 h-5 text-white" strokeWidth={2} />
                                            </div>
                                            <p className="text-sm text-gray-700 font-medium">
                                                Obtén direcciones en tiempo real
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        onClick={requestLocationPermission}
                                        disabled={loadingLocation}
                                        className={`w-full bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white py-3.5 sm:py-4 rounded-xl font-bold text-sm sm:text-base flex items-center justify-center mb-3 shadow-lg ${loadingLocation
                                            ? "opacity-50 cursor-not-allowed"
                                            : "hover:shadow-xl transition-all"
                                            }`}
                                    >
                                        {loadingLocation ? (
                                            <>
                                                <Loader className="w-5 h-5 mr-2 animate-spin" strokeWidth={2} />
                                                Obteniendo ubicación...
                                            </>
                                        ) : (
                                            <>
                                                <MapPin className="w-5 h-5 mr-2" strokeWidth={2} />
                                                Activar ubicación
                                            </>
                                        )}
                                    </button>

                                    <button
                                        onClick={() => setShowLocationModal(false)}
                                        disabled={loadingLocation}
                                        className="w-full bg-gray-100 text-gray-700 py-3.5 sm:py-4 rounded-xl font-bold text-sm sm:text-base hover:bg-gray-200 transition-all"
                                    >
                                        Continuar sin ubicación
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Stores Grid - Responsive columns */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {loadingStores ? (
                            <div className="col-span-full text-center bg-white rounded-2xl p-8 sm:p-12 shadow-lg">
                                <div className="bg-gradient-to-br from-[#FF0000] to-[#CC0000] p-5 rounded-2xl inline-block mb-4">
                                    <Loader className="w-10 h-10 sm:w-12 sm:h-12 text-white animate-spin" strokeWidth={2} />
                                </div>
                                <h3 className="text-gray-800 text-lg sm:text-xl font-bold mb-2">
                                    Cargando sucursales
                                </h3>
                                <p className="text-gray-600 text-sm">
                                    Por favor espera un momento
                                </p>
                            </div>
                        ) : error ? (
                            <div className="col-span-full text-center bg-white rounded-2xl p-8 sm:p-12 shadow-lg">
                                <div className="bg-red-100 p-5 rounded-2xl inline-block mb-4">
                                    <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-600" strokeWidth={2} />
                                </div>
                                <h3 className="text-gray-800 font-bold text-lg sm:text-xl mb-2">
                                    Error al cargar
                                </h3>
                                <p className="text-gray-600 text-sm mb-6">{error}</p>
                                <button
                                    onClick={() => {
                                        setLoadingStores(true);
                                        setError(null);
                                    }}
                                    className="bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base hover:shadow-lg transition-all"
                                >
                                    Reintentar
                                </button>
                            </div>
                        ) : filteredStores.length > 0 ? (
                            filteredStores.map((store, index) => (
                                <div
                                    key={store.id || index}
                                    id={`store-${store.id}`}
                                    onClick={() => handleStorePress(store)}
                                    className={`bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden group ${nearestStoreId === store.id
                                        ? 'ring-4 ring-[#FFBC00] shadow-2xl animate-pulse-golden'
                                        : 'hover:-translate-y-1'
                                        }`}
                                >
                                    {/* Store Photo */}
                                    <div className="relative overflow-hidden">
                                        {store.photo ? (
                                            <img
                                                src={store.photo}
                                                alt={store.storeName}
                                                className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                                            />
                                        ) : (
                                            <div className="w-full h-40 sm:h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                                <Store className="w-12 h-12 sm:w-14 sm:h-14 text-gray-400" strokeWidth={1.5} />
                                            </div>
                                        )}

                                        {/* Nearest Badge */}
                                        {nearestStoreId === store.id && (
                                            <div className="absolute top-2 right-2 bg-gradient-to-r from-[#FFBC00] to-[#FFA500] text-white px-3 py-1.5 rounded-full font-bold text-xs sm:text-sm shadow-lg flex items-center space-x-1.5">
                                                <Navigation className="w-4 h-4" strokeWidth={2} />
                                                <span>Más Cercana</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Store Content */}
                                    <div className="p-4 sm:p-6">
                                        <div className="mb-4">
                                            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 line-clamp-1">
                                                {store.storeName}
                                            </h3>
                                            <div className="flex items-center flex-wrap gap-2 text-sm">
                                                <div className="flex items-center bg-green-50 px-2 py-1 rounded-full">
                                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1.5" />
                                                    <span className="text-green-700 font-semibold text-xs">
                                                        Abierto
                                                    </span>
                                                </div>
                                                {store.distance !== undefined && (
                                                    <div className="flex items-center bg-red-50 px-2 py-1 rounded-full">
                                                        <MapPin className="w-3.5 h-3.5 text-[#FF0000] mr-1" strokeWidth={2} />
                                                        <span className="text-[#FF0000] font-bold text-xs">
                                                            {formatDistance(store.distance)}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Info Cards */}
                                        <div className="space-y-3 mb-4">
                                            <div className="flex items-start bg-gray-50 rounded-xl p-3">
                                                <div className="bg-[#FF0000] p-2 rounded-lg mr-3 flex-shrink-0">
                                                    <MapPin className="w-4 h-4 text-white" strokeWidth={2} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[10px] sm:text-xs text-[#FF0000] font-bold uppercase mb-1">
                                                        Dirección
                                                    </p>
                                                    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed line-clamp-2">
                                                        {store.address}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-start bg-gray-50 rounded-xl p-3">
                                                <div className="bg-[#FF0000] p-2 rounded-lg mr-3 flex-shrink-0">
                                                    <Phone className="w-4 h-4 text-white" strokeWidth={2} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[10px] sm:text-xs text-[#FF0000] font-bold uppercase mb-1">
                                                        Teléfono
                                                    </p>
                                                    <p className="text-xs sm:text-sm text-gray-700 font-medium">
                                                        {store.phoneNumber}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOrder(store.id);
                                                }}
                                                className="bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm hover:shadow-lg transition-all flex items-center justify-center"
                                            >
                                                <Phone className="w-4 h-4 mr-1.5" strokeWidth={2} />
                                                Ordenar
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openInMaps(store);
                                                }}
                                                className="bg-gray-100 text-gray-700 py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm hover:bg-gray-200 transition-all flex items-center justify-center"
                                            >
                                                <Navigation className="w-4 h-4 mr-1.5" strokeWidth={2} />
                                                Ir
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full text-center bg-white rounded-2xl p-8 sm:p-12 shadow-lg">
                                <div className="bg-gray-100 p-5 rounded-2xl inline-block mb-4">
                                    <Search className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" strokeWidth={2} />
                                </div>
                                <h3 className="text-gray-800 font-bold text-lg sm:text-xl mb-2">
                                    Sin resultados
                                </h3>
                                <p className="text-gray-600 text-sm mb-6">
                                    No encontramos sucursales que coincidan con "{searchQuery}"
                                </p>
                                <button
                                    onClick={() => setSearchQuery("")}
                                    className="bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base hover:shadow-lg transition-all"
                                >
                                    Limpiar búsqueda
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes slideUp {
                    0% {
                        transform: translateY(100%);
                        opacity: 0;
                    }
                    20% {
                        transform: translateY(0);
                        opacity: 1;
                    }
                    80% {
                        transform: translateY(0);
                        opacity: 1;
                    }
                    100% {
                        transform: translateY(100%);
                        opacity: 0;
                    }
                }

                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: scale(0.9) translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }

                .animate-slideUp {
                    animation: slideUp 3s ease-in-out;
                }

                .animate-slideIn {
                    animation: slideIn 0.3s ease-out;
                }

                @keyframes pulseGolden {
                    0%, 100% {
                        box-shadow: 0 0 0 0 rgba(255, 188, 0, 0.7);
                    }
                    50% {
                        box-shadow: 0 0 0 20px rgba(255, 188, 0, 0);
                    }
                }

                .animate-pulse-golden {
                    animation: pulseGolden 2s infinite;
                }
            `}</style>
        </div>
    );
};

export default Sucursales;